// Workforce Planning headcount-per-day-of-week widget.
//
// Calls /api/planning/headcount-week?week_id=… and renders, for each
// weekday, a stacked-bar row per shift start time, segmented by
// department. Counts use the EFFECTIVE department for each (worker,
// day) — per-day department overrides are honoured server-side, so a
// worker who is in Ompak on Monday but in Orderverzameling on Tuesday
// is counted under each respective department on each respective day.
//
// Compact 7-row layout fits the dashboard slot without scrolling on
// laptop screens.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarDays, Users, ArrowRight } from 'lucide-react';
import { BRAND, DEPT_COLORS } from './_constants';

const isoWeekIdNow = () => {
  // ISO week id for today — same convention as the backend.
  const now = new Date();
  // ISO week: Thursday of the same week determines the year.
  const tmp = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dow = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dow);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

export const WorkforceHeadcountWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekId, setWeekId] = useState(isoWeekIdNow);
  const [weeks, setWeeks] = useState([]);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Pull the week catalogue so the user can flip between recent +
  // upcoming weeks without touching the planning page.
  useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) return;
    fetch(`${API}/api/planning-v2/dash/weeks-with-data`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => setWeeks(d?.weeks || []))
      .catch(() => {});
  }, [API]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/api/planning-v2/dash/headcount-week?week_id=${weekId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [weekId, API]);

  // Hide if user has no planning access
  const isLocked = !!data?.locked;

  const days = data?.days || [];
  const depts = data?.departments || [];
  const total = data?.total || 0;

  // Pick a colour per dept_id (stable across rows so the planner builds
  // a mental map on first read).
  const colourFor = useMemo(() => {
    const map = {};
    depts.forEach((d, i) => { map[d.dept_id] = DEPT_COLORS[i % DEPT_COLORS.length]; });
    return map;
  }, [depts]);

  // Normalise every row against the busiest day so bar widths are
  // comparable across the week, not scaled per-row.
  const maxDayTotal = Math.max(1, ...days.map(d => d.total));

  if (isLocked) return null;

  const fmtBounds = (iso) => {
    if (!iso) return '';
    const [, m, d] = iso.split('-');
    return `${d}.${m}`;
  };

  return (
    <Card data-testid="workforce-headcount-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5" style={{ color: BRAND.cyan }} />
            Workforce Planning · weekly headcount
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={weekId} onValueChange={setWeekId}>
              <SelectTrigger className="h-8 w-32 text-xs font-mono" data-testid="wf-headcount-week">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {weeks.length === 0 && <SelectItem value={weekId}>{weekId}</SelectItem>}
                {weeks.map(w => (
                  <SelectItem key={w.week_id} value={w.week_id}>
                    {w.week_id} {w.has_schedule ? '·' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/planning" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5">
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <CardDescription className="text-xs">
          Per-day, per-start-time headcount segmented by effective department · per-day overrides honoured · counts only.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="h-48 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : total === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center text-center text-muted-foreground" data-testid="wf-headcount-empty">
            <Users className="h-8 w-8 opacity-30 mb-2" />
            <p className="text-sm">Nothing planned for {weekId} yet.</p>
            <Link to="/planning" className="text-xs text-primary hover:underline mt-1">Plan the week →</Link>
          </div>
        ) : (
          <>
            {/* Week total + dept legend */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-3xl font-bold leading-none">{total}</p>
                <p className="text-xs text-muted-foreground mt-0.5">scheduled days · {weekId}</p>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 justify-end max-w-[60%]">
                {depts.map((d) => (
                  <div key={d.dept_id} className="flex items-center gap-1.5 text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colourFor[d.dept_id] }} />
                    <span className="text-muted-foreground truncate max-w-[110px]" title={d.name}>{d.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 7 day rows — each row breaks down by start time */}
            <div className="space-y-1.5" data-testid="wf-headcount-days">
              {days.map(row => {
                const isWeekend = row.day === 5 || row.day === 6;
                const widthPct = (row.total / maxDayTotal) * 100;
                return (
                  <div
                    key={row.day}
                    className={`flex items-stretch gap-2 text-xs ${isWeekend ? 'opacity-90' : ''}`}
                    data-testid={`wf-headcount-day-${row.day}`}
                  >
                    <div className="w-20 flex flex-col justify-center flex-shrink-0">
                      <span className="font-mono font-semibold uppercase tracking-wide">{row.label}</span>
                      <span className="text-[9px] text-muted-foreground tabular-nums">{fmtBounds(row.date)}</span>
                    </div>
                    <div className="flex-1 flex flex-col justify-center min-w-0">
                      {row.total === 0 ? (
                        <div className="h-7 bg-muted/30 rounded-md flex items-center px-2 text-[10px] text-muted-foreground italic">
                          off — no one scheduled
                        </div>
                      ) : (
                        <div className="h-7 bg-muted/40 rounded-md overflow-hidden relative">
                          <div className="h-full flex" style={{ width: `${widthPct}%` }}>
                            {row.buckets.flatMap(b => b.by_department
                              .filter(dep => dep.count > 0)
                              .map(dep => ({ ...dep, start: b.start_time, bucketTotal: b.total }))
                            ).map((seg, i) => {
                              const segPct = (seg.count / row.total) * 100;
                              return (
                                <div
                                  key={`${seg.start}-${seg.dept_id}-${i}`}
                                  className="h-full flex items-center justify-center text-white text-[10px] font-semibold border-r border-white/30"
                                  style={{ width: `${segPct}%`, background: colourFor[seg.dept_id] || '#888' }}
                                  title={`${seg.start} · ${depts.find(x => x.dept_id === seg.dept_id)?.name || seg.dept_id}: ${seg.count}`}
                                >
                                  {segPct > 7 ? seg.count : ''}
                                </div>
                              );
                            })}
                          </div>
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-semibold text-foreground/80 text-[11px] tabular-nums">{row.total}</span>
                        </div>
                      )}
                      {/* Per-start-time mini summary line — keeps the
                          bar legible AND gives the exact "X (HH:MM)"
                          numbers Daniel uses for daily balance reads. */}
                      {row.buckets.length > 0 && (
                        <p className="mt-0.5 text-[10px] text-muted-foreground font-mono truncate" title={row.buckets.map(b => `${b.total} (${b.start_time})`).join(' · ')}>
                          {row.buckets.map(b => `${b.total} (${b.start_time})`).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
